import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Form } from 'semantic-ui-react'
import { DateInput } from 'semantic-ui-calendar-react'
import dayjs from 'dayjs'
import ErrorIcon from '~/pages/Farmer/ClimateCheck/AnswerClimateCheck/Steps/ErrorIcon'
import { getDateFormatted } from '../utils'
import { useAuth } from '~/routes/components/AuthProvider'

const DateFields: React.FC = () => {
  const { t } = useTranslation()
  const { control, setValue, formState: { isSubmitted } } = useFormContext()
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)

  const watchFeedingFrom = useWatch({ control, name: 'feedingFrom' })
  const watchFeedingTo = useWatch({ control, name: 'feedingTo' })
  const watchPurchaseDate = useWatch({ control, name: 'purchaseDate' })

  const handleKeyPress = (e: React.KeyboardEvent) => {
    e.preventDefault()
  }

  useEffect(() => {
    if (!watchPurchaseDate) {
      setValue('feedingFrom', '')
      return
    }

    const purchase = getDateFormatted(watchPurchaseDate)
    const feedingFrom = getDateFormatted(watchFeedingFrom)

    if (dayjs(purchase).diff(dayjs(feedingFrom)) > 0) {
      setValue('feedingFrom', '')
    }
  }, [watchPurchaseDate])

  return (
    <Form.Group widths='equal'>
      <Controller
        control={control}
        name='feedingFrom'
        rules={{
          required: {
            value: true,
            message: t('Please enter feeding from date')
          }
        }}
        render={({ fieldState }) => {
          const hasError = !watchFeedingFrom && isSubmitted
          return (
            <Form.Field error={hasError}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                {t('Feeding from')}
                <ErrorIcon control={control} name='feedingFrom' />
              </label>
              <DateInput
                name='feedingFrom'
                placeholder={t('Feeding from')}
                minDate={watchPurchaseDate}
                closable
                onKeyDown={handleKeyPress}
                markColor='blue'
                localization={languageLocal?.code}
                closeOnMouseLeave={false}
                dateFormat='DD.MM.YYYY'
                className='datePicker'
                value={watchFeedingFrom || ''}
                iconPosition='left'
                disabled={!watchPurchaseDate}
                onChange={(event, { value }) => setValue('feedingFrom', value)}
              />
            </Form.Field>
          )
        }}
      />

      <Controller
        control={control}
        name='feedingTo'
        rules={{
          required: {
            value: true,
            message: t('Please enter feeding to date')
          },
          validate: () => {
            if (!watchFeedingFrom || !watchFeedingTo) return true
            const dateFrom = getDateFormatted(watchFeedingFrom)
            const dateTo = getDateFormatted(watchFeedingTo)
            if (dateFrom && dateTo && dayjs(dateTo).diff(dayjs(dateFrom)) <= 0) {
              return t('Feeding To must be after Feeding From')
            }
            return true
          }
        }}
        render={({ fieldState }) => {
          const hasError = !watchFeedingTo && isSubmitted
          const dateFrom = getDateFormatted(watchFeedingFrom)
          const dateTo = getDateFormatted(watchFeedingTo)
          const isDateInvalid = dateFrom && dateTo && dayjs(dateTo).diff(dayjs(dateFrom)) <= 0

          return (
            <Form.Field error={hasError || isDateInvalid}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                {t('Feeding to')}
                <ErrorIcon control={control} name='feedingTo' />
              </label>
              <DateInput
                name='feedingTo'
                placeholder={t('Feeding to')}
                closable
                onKeyDown={handleKeyPress}
                markColor='blue'
                initialDate={watchFeedingFrom}
                minDate={watchFeedingFrom}
                localization={languageLocal?.code}
                closeOnMouseLeave={false}
                dateFormat='DD.MM.YYYY'
                className='datePicker'
                disabled={!watchFeedingFrom}
                value={watchFeedingTo || ''}
                iconPosition='left'
                onChange={(event, { value }) => setValue('feedingTo', value)}
              />
            </Form.Field>
          )
        }}
      />
    </Form.Group>
  )
}

export default DateFields